import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PlanWeekGrid, WeekPicker } from "@/components/PlanWeekGrid";
import { ShopToggle, planToast, usePlanWeek } from "@/components/AddToPlanButton";
import { PLAN_DAYS, PLAN_MEALS, addDays, guessMeal } from "@/lib/mealPlan";

const plural = (n, w = "recipe") => `${n} ${w}${n === 1 ? "" : "s"}`;
const MEAL_PLURAL = { breakfast: "breakfasts", lunch: "lunches", dinner: "dinners", snacks: "snacks" };
const MEAL_ONE = { breakfast: "breakfast", lunch: "lunch", dinner: "dinner", snacks: "snack" };
const btn = "w-full px-5 py-3 rounded-full bg-rf-orange text-white font-bold disabled:opacity-50 hover:bg-rf-orange/90 transition-colors";

function failNote(data) {
  if (data.errors?.length) toast.warning(`${plural(data.errors.length)} couldn't be added`, { description: data.errors[0].error });
}

function FillMode({ recipes, week, shop, onDone, initialMeal }) {
  const nav = useNavigate();
  const [meal, setMeal] = useState(() => initialMeal || guessMeal(recipes));
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(null);
  const free = PLAN_DAYS.filter((d) => !week.slotAt(d.key, meal)).length;
  const meals = MEAL_PLURAL[meal];
  const run = async (ids, weekStart) => {
    setBusy(true);
    try {
      const { data } = await api.post("/meal-plan/bulk-place", { week_start: weekStart, mode: "fill", meal_type: meal, recipe_ids: ids, shop, plan_id: week.planId });
      failNote(data);
      if (data.leftover_ids.length) {
        setLeft({ ids: data.leftover_ids, placed: data.placed, weekStart });
        toast.success(`${plural(data.placed)} placed`);
      } else {
        planToast(nav, weekStart, `${plural(data.placed)} added to your ${meals}`);
        onDone();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't fill your plan"); }
    setBusy(false);
  };
  if (left) {
    const next = addDays(left.weekStart, 7);
    return (
      <div className="space-y-3" data-testid="plan-fill-leftover">
        <p className="text-sm text-rf-ink"><b>{plural(left.placed)}</b> placed. <b>{left.ids.length}</b> didn&rsquo;t fit because that week&rsquo;s {meals} are full.</p>
        <button type="button" disabled={busy} className={btn} data-testid="plan-fill-next-week" onClick={() => { week.setWeekStart(next); run(left.ids, next); }}>
          {busy ? "Placing…" : "Use the following week too"}
        </button>
        <button type="button" data-testid="plan-fill-stop" onClick={() => { planToast(nav, left.weekStart, "Plan updated"); onDone(); }}
                className="w-full px-5 py-3 rounded-full border-2 border-rf-ink text-rf-ink font-bold bg-white hover:bg-rf-cream transition-colors">No thanks, I&rsquo;m done</button>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="plan-fill-meals">
        {PLAN_MEALS.map((m) => (
          <button key={m.key} type="button" onClick={() => setMeal(m.key)} data-testid={`plan-fill-meal-${m.key}`} aria-pressed={meal === m.key}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${meal === m.key ? "bg-rf-ink text-rf-cream" : "bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber/30"}`}>{m.label}</button>
        ))}
      </div>
      <p className="text-sm text-rf-ink-soft" data-testid="plan-fill-summary">
        {week.plan ? `${free} empty ${free === 1 ? MEAL_ONE[meal] : meals} this week. Recipes go in Mon → Sun, skipping days already planned.` : "Loading your week…"}
      </p>
      <button type="button" disabled={busy || !week.plan} className={btn} data-testid="plan-fill-confirm" onClick={() => run(recipes.map((r) => r.recipe_id), week.weekStart)}>
        {busy ? "Placing…" : `Fill ${meals} with ${plural(recipes.length)}`}
      </button>
    </div>
  );
}

function PlaceMode({ recipes, week, shop, onDone }) {
  const nav = useNavigate();
  const [active, setActive] = useState(0);
  const [assign, setAssign] = useState({}); // "day:meal" -> recipe index
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAssign({}); setActive(0); }, [week.weekStart]);
  const placedIdx = useMemo(() => new Set(Object.values(assign)), [assign]);
  const pickCell = (day, meal) => {
    const key = `${day}:${meal}`;
    const next = Object.fromEntries(Object.entries(assign).filter(([k, i]) => i !== active && k !== key));
    if (assign[key] !== active) next[key] = active;
    setAssign(next);
    const used = new Set(Object.values(next));
    const nxt = recipes.findIndex((_, i) => !used.has(i));
    if (nxt >= 0) setActive(nxt);
  };
  const cell = (day, meal) => {
    const i = assign[`${day}:${meal}`];
    const s = week.slotAt(day, meal);
    if (i !== undefined) return { text: recipes[i].title, state: s ? "replace" : "new" };
    return s ? { text: s.recipe_title, state: "taken" } : { text: "", state: "empty" };
  };
  const save = async () => {
    setBusy(true);
    try {
      const placements = Object.entries(assign).map(([k, i]) => { const [day, meal_type] = k.split(":"); return { day, meal_type, recipe_id: recipes[i].recipe_id }; });
      const { data } = await api.post("/meal-plan/bulk-place", { week_start: week.weekStart, placements, replace: true, shop, plan_id: week.planId });
      failNote(data);
      planToast(nav, week.weekStart, `${plural(data.placed)} added to your plan`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save your plan"); }
    setBusy(false);
  };
  const n = Object.keys(assign).length;
  return (
    <div className="space-y-3">
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="plan-place-recipes">
        {recipes.map((r, i) => (
          <button key={r.recipe_id} type="button" onClick={() => setActive(i)} data-testid={`plan-place-recipe-${r.recipe_id}`} aria-pressed={active === i}
                  className={`shrink-0 max-w-[180px] truncate px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${active === i ? "bg-rf-orange text-white border-rf-orange" : placedIdx.has(i) ? "bg-rf-amber/20 text-rf-ink border-rf-amber" : "bg-white text-rf-ink-soft border-rf-ink/15"}`}>
            {placedIdx.has(i) ? "✓ " : ""}{r.title}
          </button>
        ))}
      </div>
      <p className="text-xs text-rf-ink-soft">Tap a square to put <b>{recipes[active]?.title}</b> there. Tap again to remove it. Red squares replace what&rsquo;s already planned.</p>
      {week.plan ? <PlanWeekGrid weekStart={week.weekStart} cell={cell} onPick={pickCell} /> : <p className="text-sm text-rf-ink-soft py-8 text-center">Loading your week…</p>}
      <button type="button" disabled={!n || busy} className={btn} onClick={save} data-testid="plan-place-confirm">
        {busy ? "Saving…" : n ? `Save ${plural(n)} to the plan` : "Tap squares to place recipes"}
      </button>
    </div>
  );
}

export function BulkPlanDialog({ open, onOpenChange, recipes, onDone, initialWeek, initialMeal, planId }) {
  const week = usePlanWeek(open, initialWeek, planId);
  const [mode, setMode] = useState("fill");
  const [shop, setShop] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[calc(100%-1.5rem)] rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="bulk-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Add {plural(recipes.length)} to your meal plan</DialogTitle>
          <DialogDescription>Fill one meal across the week, or place each recipe yourself.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-rf-cream-alt">
          {[["fill", "Fill for me"], ["place", "Place each one"]].map(([m, l]) => (
            <button key={m} type="button" onClick={() => setMode(m)} data-testid={`bulk-plan-mode-${m}`} aria-pressed={mode === m}
                    className={`py-2 rounded-full text-sm font-bold transition-colors ${mode === m ? "bg-white shadow text-rf-ink" : "text-rf-ink-soft"}`}>{l}</button>
          ))}
        </div>
        <WeekPicker weekStart={week.weekStart} onChange={week.setWeekStart} />
        <ShopToggle plan={week.plan} shop={shop} setShop={setShop} />
        {mode === "fill" ? <FillMode recipes={recipes} week={week} shop={shop} onDone={onDone} initialMeal={initialMeal} />
          : <PlaceMode recipes={recipes} week={week} shop={shop} onDone={onDone} />}
      </DialogContent>
    </Dialog>
  );
}
